import { __rest, __assign } from 'tslib';
import { useLocalization } from '@shared-ui/localization-context';
import * as React from 'react';
import { useRef, useState, useEffect } from 'react';
import { UitkLink } from 'uitk-react-link';
import { classNames, useUitkContext, UitkContextProvider } from 'uitk-react-utils';
import { UitkExpandoTriggerStates } from './types.esm.js';
import styles from './uitk-expando.css';

var getClassNameList = classNames.bind(styles);
var ExpandoPeek = function (props) {
    var _a;
    var _b = props.isVisible, isVisible = _b === void 0 ? false : _b, expandLabel = props.expandLabel, expandA11yLabel = props.expandA11yLabel, collapseLabel = props.collapseLabel, collapseA11yLabel = props.collapseA11yLabel, l10n = props.l10n, handleClick = props.handleClick, hideScrim = props.hideScrim, analytics = props.analytics, className = props.className, children = props.children, lines = props.lines, items = props.items, peekControlRef = props.peekControlRef, _c = props.whitespace, whitespace = _c === void 0 ? true : _c, revealRef = props.revealRef, attrs = __rest(props, ["isVisible", "expandLabel", "expandA11yLabel", "collapseLabel", "collapseA11yLabel", "l10n", "handleClick", "hideScrim", "analytics", "className", "children", "lines", "items", "peekControlRef", "whitespace", "revealRef"]);
    var forwardContext = useUitkContext();
    var formatText = useLocalization().formatText;
    var innerDomRef = useRef(null);
    var _d = useState({
        scrollHeight: 0,
        clientHeight: 0
    }), expandoHeightRef = _d[0], setExpandoHeightRef = _d[1];
    var expandoPeekInnerClassName = getClassNameList('uitk-expando-peek-inner', {
        'display-lines': lines,
        'display-items': items
    });
    useEffect(function () {
        var _a, _b;
        (innerDomRef === null || innerDomRef === void 0 ? void 0 : innerDomRef.current) &&
            setExpandoHeightRef({
                scrollHeight: (_a = innerDomRef === null || innerDomRef === void 0 ? void 0 : innerDomRef.current) === null || _a === void 0 ? void 0 : _a.scrollHeight,
                clientHeight: (_b = innerDomRef === null || innerDomRef === void 0 ? void 0 : innerDomRef.current) === null || _b === void 0 ? void 0 : _b.clientHeight
            });
    }, [innerDomRef]);
    useEffect(function () {
        if (isVisible) {
            revealRef === null || revealRef === void 0 ? void 0 : revealRef.current.focus();
        }
    }, [isVisible, revealRef]);
    var renderExpandoButton = function () {
        var btnText = isVisible
            ? collapseLabel || l10n.formatText('expando.collapseLabel')
            : expandLabel || l10n.formatText('expando.expandLabel');
        var btnA11yText = isVisible
            ? collapseA11yLabel || l10n.formatText('expando.collapseLabel')
            : expandA11yLabel || l10n.formatText('expando.expandLabel');
        var localAnalyticsId = isVisible
            ? UitkExpandoTriggerStates.get('collapse')
            : UitkExpandoTriggerStates.get('expand');
        var btnClass = getClassNameList('uitk-expando-peek-link');
        var controlClasses = getClassNameList('uitk-expando-peek-control', {
            'uitk-expando-peek-control-active': isVisible
        });
        return (React.createElement("div", { className: controlClasses },
            React.createElement(UitkLink, { "aria-expanded": isVisible, className: btnClass, analytics: analytics && __assign({ localAnalyticsId: localAnalyticsId }, analytics), domRef: peekControlRef },
                React.createElement("button", { type: "button", onClick: handleClick, "aria-label": btnA11yText }, btnText))));
    };
    var wrapperClasses = getClassNameList('uitk-expando-peek', className);
    var childCount = React.Children.count(children);
    if (childCount === 1) {
        childCount = React.Children.count(children.props.children);
    }
    var expandoCustomProperties = {
        '--uitk-expando-peek-lines-to-clamp': lines
    };
    // Disable if content is smaller than the expandedHeight
    var showExpando = !!((lines && expandoHeightRef.clientHeight < expandoHeightRef.scrollHeight) ||
        (items && items < childCount));
    var mainClass = 'uitk-expando-peek-main';
    var mainClasses = getClassNameList(mainClass, (_a = {},
        _a["".concat(mainClass, "-active")] = isVisible,
        _a["".concat(mainClass, "-inactive")] = !isVisible && showExpando,
        _a['hide-scrim'] = hideScrim,
        _a["".concat(mainClass, "-whitespace")] = isVisible && whitespace,
        _a));
    var contextData = __assign({ collapsed: !isVisible, srMessage: formatText('radio.button.hidden.items', items, childCount) }, forwardContext.data);
    return (React.createElement(React.Fragment, null,
        React.createElement("div", __assign({ className: wrapperClasses }, attrs),
            React.createElement("div", { className: mainClasses },
                React.createElement("div", { className: expandoPeekInnerClassName, ref: innerDomRef, style: expandoCustomProperties, "data-items-to-display": isVisible ? undefined : items },
                    React.createElement(UitkContextProvider, { config: { context: 'uitk-expando-peek-context', data: contextData } }, children))),
            showExpando && renderExpandoButton())));
};
var UitkExpandoPeek = function (props) {
    var l10n = useLocalization();
    return React.createElement(ExpandoPeek, __assign({ l10n: l10n }, props));
};

export { ExpandoPeek, UitkExpandoPeek };
